import { useState, useRef, useEffect } from 'react'
import { useFilterPanel } from './FilterPanelContext'
import { FilterTag } from 'components/atoms/FilterTag/FilterTag'

export interface SelectOption {
    value: string
    label: string
    /** Datos adicionales que se incluyen en el onApply del panel sin necesidad de resolverlos fuera */
    meta?: Record<string, unknown>
}

interface Props {
    name: string
    label: string
    options: SelectOption[]
    disabled?: boolean
}

export const FilterSelect = ({ name, label, options, disabled }: Props) => {
    const { values, setValue, clearValue } = useFilterPanel()
    const [open, setOpen] = useState(false)
    const dropdownRef = useRef<HTMLDivElement>(null)
    const selected = (values[name] as string) ?? ''
    const selectedLabel = options.find((o) => o.value === selected)?.label ?? selected

    // Cierra el dropdown al hacer click fuera (punto 10)
    useEffect(() => {
        const handleClickOutside = (e: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
                setOpen(false)
            }
        }
        if (open) document.addEventListener('mousedown', handleClickOutside)
        return () => document.removeEventListener('mousedown', handleClickOutside)
    }, [open])

    return (
        <div className="flex flex-col gap-[4px]">
            <div className="flex items-center justify-between">
                <span className="text-[14px] leading-[18px] text-[#3E5060]">{label}</span>
                {selected && (
                    <button
                        type="button"
                        onClick={() => {
                            clearValue(name)
                            clearValue(`${name}__meta`)
                        }}
                        className="text-[14px] leading-[18px] font-semibold text-[#568C13] hover:text-green-700"
                    >
                        Limpiar
                    </button>
                )}
            </div>

            <div className="relative" ref={dropdownRef}>
                <button
                    type="button"
                    onClick={() => !disabled && setOpen((o) => !o)}
                    disabled={disabled}
                    className="w-full flex items-center justify-between gap-2 px-[12px] py-[6px] rounded-[8px] border border-[#CBD5E1] bg-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                    <span className={`text-[16px] leading-[20px] ${selected ? 'text-[#3E5060]' : 'text-[#94A3B8]'}`}>
                        {selected ? selectedLabel : 'Seleccionar'}
                    </span>
                    <svg
                        className={`w-6 h-6 shrink-0 text-[#94A3B8] transition-transform ${open ? 'rotate-180' : ''}`}
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                    >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                </button>

                {open && (
                    <div className="absolute z-20 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg max-h-48 overflow-y-auto">
                        {options.length === 0 && (
                            <p className="px-3 py-2 text-sm text-gray-400">Sin opciones</p>
                        )}
                        {options.map((opt) => (
                            <label
                                key={opt.value}
                                className="flex items-center gap-2 px-3 py-2 text-sm text-gray-700 cursor-pointer hover:bg-gray-50"
                            >
                                <input
                                    type="radio"
                                    name={name}
                                    checked={selected === opt.value}
                                    onChange={() => {
                                        setValue(name, opt.value)
                                        // Guarda el meta de la opción seleccionada para que el
                                        // padre pueda leerlo en onApply sin resolver la opción externamente
                                        if (opt.meta) setValue(`${name}__meta`, opt.meta)
                                        else clearValue(`${name}__meta`)
                                        setOpen(false)
                                    }}
                                    className="accent-green-600"
                                />
                                {opt.label}
                            </label>
                        ))}
                    </div>
                )}
            </div>

            {selected && (
                <div className="flex flex-wrap gap-2">
                    <FilterTag
                        label={selectedLabel}
                        onRemove={() => {
                            clearValue(name)
                            clearValue(`${name}__meta`)
                        }}
                    />
                </div>
            )}
        </div>
    )
}
